import { FileText, Video, Scissors, BarChart3, UserPlus, MessageSquare } from "lucide-react";
import { FadeUp, SectionHead } from "./Motion";

const SERVICES = [
  { t: "Scripting", d: "We develop advertising scripts tailored to your business.", I: FileText, span: "lg:col-span-4", art: "lines" },
  { t: "Filming", d: "We create professional video content that showcases your services.", I: Video, span: "lg:col-span-2", art: "rec" },
  { t: "Editing", d: "We turn raw footage into polished advertisements.", I: Scissors, span: "lg:col-span-2", art: "timeline" },
  { t: "Campaign Management", d: "We manage your Facebook and Instagram advertising campaigns.", I: BarChart3, span: "lg:col-span-2", art: "bars" },
  { t: "Lead Generation", d: "We build campaigns designed to generate qualified inquiries.", I: UserPlus, span: "lg:col-span-2", art: "ping" },
  { t: "Automated Follow-Up", d: "Available on qualifying packages, our systems help you respond to and follow up with incoming leads.", I: MessageSquare, span: "lg:col-span-6", art: "chat" },
];

function Art({ kind }) {
  if (kind === "lines") return (
    <div className="space-y-2">{[90, 70, 82, 45].map((w, i) => (
      <div key={i} className="h-2 origin-left rounded-full bg-white/10 transition-transform duration-700 group-hover:bg-electric/40" style={{ width: `${w}%`, transitionDelay: `${i * 80}ms` }} />
    ))}</div>
  );
  if (kind === "rec") return <div className="flex items-center gap-2 font-mono text-xs text-slate-300"><span className="h-3 w-3 animate-pulse rounded-full bg-red-500" /> REC 00:00:24</div>;
  if (kind === "timeline") return (
    <div className="flex gap-1">{[30, 22, 26, 18].map((w, i) => <span key={i} className="h-6 rounded-sm border border-electric/40 bg-electric/15 transition-transform duration-500 group-hover:-translate-y-1" style={{ width: `${w}%`, transitionDelay: `${i * 60}ms` }} />)}</div>
  );
  if (kind === "bars") return (
    <div className="flex h-12 items-end gap-1.5">{[30, 45, 40, 62, 58, 80, 92].map((h, i) => <span key={i} className="w-3 origin-bottom rounded-sm bg-gradient-to-t from-electric to-cyan-300 opacity-60 transition-transform duration-500 group-hover:scale-y-110 group-hover:opacity-100" style={{ height: `${h}%` }} />)}</div>
  );
  if (kind === "ping") return <span className="relative flex h-4 w-4"><span className="absolute inline-flex h-full w-full animate-ping2 rounded-full bg-cyan-300" /><span className="relative inline-flex h-4 w-4 rounded-full bg-cyan-300" /></span>;
  return (
    <div className="flex flex-wrap gap-2">
      {["Day 1", "Day 2", "Day 3", "Day 4", "Day 5", "Day 6", "Day 7"].map((d, i) => (
        <span key={d} className="rounded-full border border-white/10 px-3 py-1 font-mono text-[11px] text-slate-300 transition-colors duration-300 group-hover:border-electric/50 group-hover:text-white" style={{ transitionDelay: `${i * 50}ms` }}>{d}</span>
      ))}
    </div>
  );
}

export function Services() {
  return (
    <section id="services" className="relative py-24 lg:py-36" data-testid="services-section">
      <div className="container-x">
        <SectionHead eyebrow="Services" title={["Everything You Need.", "Nothing You Have to Manage."]} />
        <div className="mt-16 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-6">
          {SERVICES.map(({ t, d, I, span, art }, i) => (
            <FadeUp key={t} delay={i * 0.06} className={`${span} ${i === 5 ? "sm:col-span-2" : ""}`}>
              <article data-testid={`service-card-${i + 1}`} tabIndex={0}
                className="group relative flex h-full min-h-[240px] flex-col justify-between overflow-hidden rounded-3xl border border-white/[0.08] bg-panel/70 p-7 transition-[border-color,transform,background-color] duration-500 hover:-translate-y-1 hover:border-electric/40 hover:bg-panel focus-visible:border-electric/60">
                <div className="pointer-events-none absolute -right-24 -top-24 h-56 w-56 rounded-full bg-electric/0 blur-3xl transition-colors duration-700 group-hover:bg-electric/20" />
                <div className="flex items-start justify-between">
                  <span className="grid h-11 w-11 place-items-center rounded-xl border border-white/10 bg-white/[0.03] text-cyan-300"><I className="h-5 w-5" /></span>
                  <span className="font-mono text-xs text-slate-500">{String(i + 1).padStart(2, "0")}</span>
                </div>
                <div className="my-6"><Art kind={art} /></div>
                <div>
                  <h3 className="text-xl font-semibold tracking-tight text-white">{t}</h3>
                  <p className="mt-2 max-w-md text-sm leading-relaxed text-slate-400">{d}</p>
                </div>
              </article>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}
